import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { FormControl, FormControlLabel, FormLabel, InputAdornment, Radio, RadioGroup, TextField } from '@mui/material';
import Icon from '../../../base/icons/components/Icon';
import { IconArrowDown, IconArrowUp, IconClear, IconDeletePolls } from '../../../base/icons/svg';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import Tooltip from '../../../base/tooltip/components/Tooltip';
import Button from '../../../base/ui/components/web/Button';
import ClickableIcon from '../../../base/ui/components/web/ClickableIcon';
import Switch from '../../../base/ui/components/web/Switch';
import { BUTTON_TYPES } from '../../../base/ui/constants.web';
import { editPoll as editPollFunc } from '../../actions';
import { getPoll } from '../../functions';
import AbstractPollCreate from '../AbstractPollCreate';
import { convertHourAndMinToSecond, convertSecondToHourAndMinute, TimePickerCC } from './TimePickerCC';
import { useTranslation } from 'react-i18next';
const useStyles = makeStyles()(theme => {
    return {
        container: {
            height: '100%',
            position: 'relative'
        },
        createContainer: {
            padding: '0 24px',
            height: 'calc(100% - 120px)',
            overflowY: 'auto'
        },
        createContainerWithOptions: {
            height: 'calc(100% - 49%)',
            '@media (max-height: 600px)': {
                height: '0% !important'
            }
        },
        header: {
            ...withPixelLineHeight(theme.typography.heading6),
            color: theme.palette.default00,
            margin: '11px 0',
            fontWeight: 700,
        },
        headerAnswer: {
            margin: 0,
            marginBottom: 12,
            fontSize: '14px'
        },
        questionContainer: {
            paddingBottom: '16px',
            '& .MuiFormControlLabel-label': {
                fontSize: '14px !important'
            }
        },
        answerList: {
            listStyleType: 'none',
            margin: 0,
            padding: 0
        },
        answer: {
            marginBottom: '12px',
            '& .MuiFormControlLabel-label': {
                fontSize: '14px !important'
            }
        },
        removeOption: {
            ...withPixelLineHeight(theme.typography.bodyShortRegular),
            color: theme.palette.redP01,
            marginTop: '8px',
            border: 0,
            background: 'transparent'
        },
        addButtonContainer: {
            display: 'flex',
            padding: '8px 0',
            '& > button': {
                height: 'auto',
                padding: '4px 15px',
                width: '100%'
            }
        },
        footer: {
            position: 'absolute',
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            padding: '0 0 24px 24px',
            width: '100%',
            boxSizing: 'border-box',
        },
        footerWithOptions: {
            height: '49%',
            '@media (max-height: 600px)': {
                height: '100% !important'
            }
        },
        optionsContainer: {
            borderTop: '1px solid #E9E9E9',
            paddingTop: 12,
            overflow: 'hidden'
        },
        headerOptions: {
            display: 'flex',
            justifyContent: 'space-between',
            cursor: 'pointer',
            fontSize: '14px',
            paddingRight: '24px',
            '& svg': {
                fill: '#73777A !important'
            }
        },
        itemOptions: {
            display: 'flex',
            justifyContent: 'space-between',
            paddingLeft: 20,
            height: 40,
            '&#timeOptionsContainer': {
                height: 'auto'
            }
        },
        timesOptions: {
            height: 'auto',
            minHeight: 40,
            margin: 0,
            '& .MuiFormControlLabel-label': {
                whiteSpace: 'normal',
                fontSize: '14px'
            },
            '& .MuiRadio-root': {
                padding: 8
            }
        },
        buttonsContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            marginRight: '24px'
        },
        buttonMargin: {
            marginRight: theme.spacing(3)
        },
        optionsList: {
            height: 0,
            opacity: 0,
            paddingRight: '24px',
            '& .MuiFormControlLabel-label': {
                fontSize: '14px'
            },
        },
        showOptions: {
            height: 'auto',
            opacity: 1,
            transition: 'all 1s',
            overflow: 'auto',
            '& .MuiFormControlLabel-label': {
                whiteSpace: 'normal',
                fontSize: '14px'
            },
            '& .showOptionsPoll': {
                margin: '0px !important'
            }
        },
        setTime: {
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            fontSize: '14px'
        }
    };
});
const PollCreate = ({ addAnswer, answers, editingPoll, editingPollId, isSubmitDisabled, onSubmit, question, removeAnswer, setAnswer, setCreateMode, setQuestion, options, setOptions, onSubmitUpdatePoll, setAnswers, duration, handleDuration, validateQuestion, clickSubmit }) => {
    const { cx, classes } = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [showOptions, setShowOptions] = useState(false);
    const editPoll = useSelector((state) => state['features/polls']).pollId;
    const poll = useSelector(getPoll(editPoll));
    const [checkValidEndTimeSecond, setCheckValidEndTimeSecond] = useState(true);
    const [checkDurationMax, setCheckDurationMax] = useState(true);
    const [checkDurationMin, setCheckDurationMin] = useState(true);
    const [checkValidStartTimeSecond, setCheckValidStartTimeSecond] = useState(true);
    /*
     * This ref stores the Array of answer input fields, allowing us to focus on them.
     * This array is maintained by registerfieldRef and the useEffect below.
     */
    const answerInputs = useRef([]);
    const registerFieldRef = useCallback((i, r) => {
        if (r === null) {
            return;
        }
        answerInputs.current[i] = r;
    }, [answerInputs]);
    useEffect(() => {
        answerInputs.current = answerInputs.current.slice(0, answers.length);
    }, [answers]);
    /*
     * This state allows us to requestFocus asynchronously, without having to worry
     * about whether a newly created input field has been rendered yet or not.
     */
    const [lastFocus, requestFocus] = useState(null);
    useEffect(() => {
        if (lastFocus === null) {
            return;
        }
        const input = answerInputs.current[lastFocus];
        if (input === undefined) {
            return;
        }
        input.focus();
    }, [lastFocus]);
    useEffect(() => {
        try {
            if (poll) {
                setQuestion(poll?.question);
                const answerConverts = poll?.answers.map((item) => ({ name: item?.name }));
                setAnswers(answerConverts);
                setOptions({
                    chooseMutiple: poll?.chooseMultipleOptions,
                    addMoreOptions: poll?.canAddOption,
                    voteAnonymous: poll?.anonymousPoll,
                    setUpTime: poll?.duration !== -1 ? true : false
                });
                if (poll?.duration !== -1) {
                    handleDuration(poll?.duration);
                }
            }
        }
        catch (error) {
        }
    }, []);
    const checkModifiers = useCallback(ev => {
        // Composition events used to add accents to characters
        // despite their absence from standard US keyboards,
        // to build up logograms of many Asian languages
        // from their base components or categories and so on.
        if (ev.isComposing || ev.keyCode === 229) {
            // keyCode 229 means that user pressed some button,
            // but input method is still processing that.
            // This is a standard behavior for some input methods
            // like entering japanese or сhinese hieroglyphs.
            return true;
        }
        // Because this isn't done automatically on MacOS
        if (ev.key === 'Enter' && ev.metaKey) {
            ev.preventDefault();
            onSubmit();
            return;
        }
        if (ev.ctrlKey || ev.metaKey || ev.altKey || ev.shiftKey) {
            return;
        }
    }, []);
    const onQuestionKeyDown = useCallback(ev => {
        if (checkModifiers(ev)) {
            return;
        }
        if (ev.key === 'Enter') {
            requestFocus(0);
            ev.preventDefault();
        }
    }, []);
    // Called on keypress in answer fields
    const onAnswerKeyDown = useCallback((i, ev) => {
        if (checkModifiers(ev)) {
            return;
        }
        if (ev.key === 'Enter') {
            // We add a new option input
            // only if we are on the last option input
            if (i === answers.length - 1 && answers.length < 10) {
                addAnswer(i + 1);
            }
            requestFocus(i + 1);
            ev.preventDefault();
        }
        else if (ev.key === 'Backspace' && ev.target.value === '' && answers.length > 1) {
            removeAnswer(i);
            requestFocus(i > 0 ? i - 1 : 0);
            ev.preventDefault();
        }
        else if (ev.key === 'ArrowDown') {
            if (i === answers.length - 1) {
                addAnswer();
            }
            requestFocus(i + 1);
            ev.preventDefault();
        }
        else if (ev.key === 'ArrowUp') {
            if (i === 0) {
                addAnswer(0);
                requestFocus(0);
            }
            else {
                requestFocus(i - 1);
            }
            ev.preventDefault();
        }
    }, [answers, addAnswer, removeAnswer, requestFocus]);
    const closeEdit = () => {
        dispatch(editPollFunc(''));
    };
    const handleCancel = () => {
        if (editPoll) {
            closeEdit();
        }
        else {
            setCreateMode(false);
        }
    };
    /* eslint-disable react/jsx-no-bind */
    return (React.createElement("form", { className: classes.container, onSubmit: onSubmit },
        React.createElement("div", { className: cx(classes.createContainer, showOptions && classes.createContainerWithOptions) },
            React.createElement("div", { className: classes.header }, t('polls.create.create')),
            React.createElement("div", { className: classes.questionContainer },
                React.createElement(FormControlLabel, { id: 'polls-create-input', label: t('polls.create.pollQuestion'), labelPlacement: 'top', required: true, control: React.createElement(TextField, { size: "small", placeholder: t('polls.create.questionPlaceholder'), autoFocus: true, value: question, onChange: (e) => setQuestion(e.target.value), onKeyPress: onQuestionKeyDown, multiline: true, maxRows: 3, fullWidth: true, sx: {
                            '& > .MuiInputBase-root': { padding: '0 8px 0 0' },
                            '& .MuiInputBase-root': {
                                '& ::placeholder': {
                                    color: '#73777A',
                                    opacity: '1'
                                },
                                '& .MuiInputBase-input': {
                                    padding: '4.5px 0.875em',
                                    fontSize: '14px',
                                }
                            }
                        }, error: clickSubmit && (validateQuestion.empty || validateQuestion.overMaxLength), helperText: clickSubmit && ((validateQuestion.empty && t('polls.errors.enterQuestion')) || (validateQuestion.overMaxLength && t('polls.errors.overLength'))), InputProps: {
                            endAdornment: (React.createElement(React.Fragment, null, question &&
                                React.createElement(InputAdornment, { position: 'end' },
                                    React.createElement(Tooltip, { content: t('textField.clear') },
                                        React.createElement(ClickableIcon, { accessibilityLabel: 'clear-input', icon: IconClear, onClick: () => setQuestion(''), className: 'icon-input', onKeyPress: (e) => {
                                                if (e?.key === 'Enter')
                                                    setQuestion('');
                                            } })))))
                        } }) })),
            React.createElement("ol", { className: classes.answerList },
                React.createElement("div", { className: cx(classes.header, classes.headerAnswer) }, t('polls.create.answerLabel')),
                answers.map((answer, i) => {
                    const isIdenticalAnswer = answers.slice(0, i).length === 0 ? false
                        : answers.slice(0, i).some(prevAnswer => prevAnswer.name === answer.name
                            && prevAnswer.name !== '' && answer.name !== '');
                    const isEmptyAnswer = answer.name.trim().length === 0;
                    const isOverLength = answer.name.trim().length > 255;
                    return (React.createElement("li", { className: classes.answer, key: i },
                        React.createElement(FormControlLabel, { id: `polls-answer-input-${i}`, label: t('polls.create.pollOption', { index: i + 1 }), labelPlacement: 'top', required: true, ref: r => registerFieldRef(i, r), control: React.createElement(TextField, { fullWidth: true, sx: {
                                    '& > .MuiInputBase-root': { padding: '0 8px 0 0' },
                                    '& .MuiInputBase-root': {
                                        '& ::placeholder': {
                                            color: '#73777A',
                                            opacity: '1'
                                        },
                                        '& .MuiInputBase-input': {
                                            padding: '4.5px 0.875em',
                                            fontSize: '14px',
                                        }
                                    }
                                }, value: answer.name, onChange: (e) => setAnswer(i, {
                                    name: e.target.value,
                                    voters: []
                                }), onKeyPress: ev => onAnswerKeyDown(i, ev), placeholder: t('polls.create.answerPlaceholder', { index: i + 1 }), multiline: true, maxRows: 3, error: isIdenticalAnswer || (isEmptyAnswer && clickSubmit) || (isOverLength && clickSubmit), helperText: (isIdenticalAnswer ?
                                    t('polls.errors.notUniqueOption', { index: i + 1 })
                                    : '')
                                    ||
                                        ((isEmptyAnswer && clickSubmit) ? t('polls.errors.enterAnswer') : '')
                                    ||
                                        ((isOverLength && clickSubmit) ? t('polls.errors.overLength') : ''), InputProps: {
                                    endAdornment: (React.createElement(React.Fragment, null, (answer.name || answers.length > 2) &&
                                        React.createElement(InputAdornment, { position: 'end' },
                                            answer.name &&
                                                React.createElement(Tooltip, { content: t('textField.clear') },
                                                    React.createElement(ClickableIcon, { accessibilityLabel: 'clear-input', icon: IconClear, onClick: () => setAnswer(i, {
                                                            name: '',
                                                            voters: []
                                                        }), className: 'icon-input', onKeyPress: (e) => {
                                                            if (e?.key === 'Enter')
                                                                setAnswer(i, {
                                                                    name: '',
                                                                    voters: []
                                                                });
                                                        } })),
                                            answers.length > 2 &&
                                                React.createElement("div", { style: { marginLeft: '4px' } },
                                                    React.createElement(Tooltip, { content: t('textField.deleteAnswer') },
                                                        React.createElement(ClickableIcon, { accessibilityLabel: 'clear-input', icon: IconDeletePolls, onClick: () => removeAnswer(i), className: 'icon-input', onKeyPress: (e) => {
                                                                if (e?.key === 'Enter')
                                                                    setAnswer(i, {
                                                                        name: '',
                                                                        voters: []
                                                                    });
                                                            } }))))))
                                } }) })));
                })),
            answers.length < 10 &&
                React.createElement("div", { className: classes.addButtonContainer },
                    React.createElement(Button, { size: 'smallMedium', accessibilityLabel: t('polls.create.addOption'), disabled: false, labelKey: 'polls.create.addOption', onClick: () => {
                            addAnswer();
                            requestFocus(answers.length);
                        }, type: BUTTON_TYPES.SECONDARY }))),
        React.createElement("div", { className: cx(classes.footer, showOptions && classes.footerWithOptions) },
            React.createElement("div", { className: cx(classes.optionsContainer, showOptions && classes.showOptions) },
                React.createElement("div", { className: `${cx(classes.header, classes.headerAnswer, classes.headerOptions)} showOptionsPoll`, onClick: () => setShowOptions(prev => !prev) },
                    t('polls.create.options'),
                    React.createElement(Icon, { size: 24, src: showOptions ? IconArrowDown : IconArrowUp })),
                React.createElement("div", { className: cx(classes.optionsList, showOptions && classes.showOptions) },
                    React.createElement(FormControlLabel, { className: cx(classes.itemOptions), labelPlacement: 'start', label: t('polls.create.multipleAnswer'), control: React.createElement(Switch, { checked: options.chooseMutiple, onChange: (value) => { setOptions({ ...options, chooseMutiple: value }); } }) }),
                    React.createElement(FormControlLabel, { className: cx(classes.itemOptions), labelPlacement: 'start', label: t('polls.create.otherAnswer'), control: React.createElement(Switch, { checked: options.addMoreOptions, onChange: (value) => { setOptions({ ...options, addMoreOptions: value }); } }) }),
                    React.createElement(FormControlLabel, { className: cx(classes.itemOptions), labelPlacement: 'start', label: t('polls.create.anonymousPoll'), control: React.createElement(Switch, { checked: options.voteAnonymous, onChange: (value) => { setOptions({ ...options, voteAnonymous: value }); } }) }),
                    React.createElement(FormControl, { id: "timeOptionsContainer", className: cx(classes.itemOptions), sx: { height: 'auto' } },
                        React.createElement(FormLabel, { className: cx(classes.setTime) }, t('polls.create.setTime')),
                        React.createElement(RadioGroup, { "aria-labelledby": "radio-buttons-group-label", defaultValue: false, name: "radio-buttons-group", value: options.setUpTime, onChange: (event) => setOptions((prev) => ({ ...prev, setUpTime: event.target.value === "true" })) },
                            React.createElement(FormControlLabel, { className: cx(classes.timesOptions), value: false, control: React.createElement(Radio, null), label: t('polls.create.ownerEnded') }),
                            React.createElement("div", { style: { display: 'flex', alignItems: 'center', marginBottom: '8px' } },
                                React.createElement(FormControlLabel, { className: cx(classes.timesOptions), value: true, control: React.createElement(Radio, null), label: !options.setUpTime ? t('polls.create.customTime', { time: convertSecondToHourAndMinute(duration) }) : '' }),
                                options.setUpTime &&
                                    React.createElement(React.Fragment, null,
                                        React.createElement(TimePickerCC, { id: 'timepicker-end', validTime: true ? (checkValidEndTimeSecond && checkDurationMin && checkDurationMax) : true, value: convertSecondToHourAndMinute(duration), setValue: (_time) => {
                                                let time = convertHourAndMinToSecond(_time);
                                                handleDuration(time);
                                            } }),
                                        React.createElement("div", { style: { marginLeft: '8px', color: '#44494D', fontSize: '14px', fontWeight: '500' } }, t('polls.create.customTime')))))))),
            React.createElement("div", { className: classes.buttonsContainer },
                React.createElement(Button, { size: 'bMedium', className: classes.buttonMargin, labelKey: 'polls.create.cancel', onClick: () => {
                        handleCancel();
                    }, type: BUTTON_TYPES.SECONDARY }),
                React.createElement(Button, { size: 'bMedium', onClick: (e) => {
                        if (editPoll != '') {
                            onSubmitUpdatePoll(e, poll?.pollId || '');
                        }
                        else {
                            onSubmit();
                        }
                    }, labelKey: editPoll != '' ? 'polls.update.update' : 'polls.create.send' })))));
};
/*
 * We apply AbstractPollCreate to fill in the AbstractProps common
 * to both the web and native implementations.
 */
// eslint-disable-next-line new-cap
export default AbstractPollCreate(PollCreate);
